import ReviewAPI from "@api/Reviews.api";
import type { IReview } from "@api/models/IReview";
import { EBreakpoints, useBreakpoint } from "@hooks/useBreakpoint";
import { Grid, Stack } from "@mantine/core";
import CircleLoader from "@modules/loader/CircleLoader";
import {
  ELoadingOptions,
  removeLoader,
  setLoader,
} from "@modules/loader/useLoader.hook";
import useLanguageStore from "@store/language.store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReviewHelper } from "../utils/Review.helper";
import Review from "./Review";

const COLUMNS_AT_BREAKTPOINT = {
  [EBreakpoints.XL]: 3,
  [EBreakpoints.LG]: 3,
  [EBreakpoints.MD]: 3,
  [EBreakpoints.SM]: 2,
  [EBreakpoints.XS]: 2,
  [EBreakpoints.BASE]: 1,
};

const FIRST_PAGE_INDEX = 1;

const ReviewList = () => {
  const { breakpoint } = useBreakpoint();
  const [reviews, setReviews] = useState<IReview[]>(
    ReviewHelper.LOADING_REVIEWS,
  );
  const page = useRef(FIRST_PAGE_INDEX);
  const lastPage = useRef(FIRST_PAGE_INDEX);
  const isFetchingRef = useRef<boolean>(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { language } = useLanguageStore();

  const getReviews = useCallback(
    (callback: (reviews: IReview[]) => void) => {
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);

      ReviewAPI.getReviews(page.current)
        .then((res) => {
          page.current = res.meta.currentPage + 1;
          lastPage.current = res.meta.lastPage;
          callback(res.data);
        })
        .finally(() => {
          isFetchingRef.current = false;
          setIsLoading(false);
        });
    },
    [language],
  );

  useEffect(() => {
    if (isFetchingRef.current) {
      return;
    }

    setLoader(ELoadingOptions.SKELETON);
    page.current = FIRST_PAGE_INDEX;
    setReviews(ReviewHelper.LOADING_REVIEWS);

    getReviews((res) => {
      setReviews(res);
      removeLoader(ELoadingOptions.SKELETON);
    });
  }, [getReviews, language]);

  const setLastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node) {
        return;
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (page.current <= lastPage.current) {
                getReviews((res) => setReviews((prev) => [...prev, ...res]));
              }
            }
          }
        },
        { root: null, rootMargin: "0px", threshold: 0.1 },
      );

      observerRef.current.observe(node);
    },
    [getReviews],
  );

  const reviewsToRender = useMemo(() => {
    return ReviewHelper.splitReviews(
      reviews,
      COLUMNS_AT_BREAKTPOINT[breakpoint],
    );
  }, [breakpoint, reviews]);

  return (
    <div className="relative">
      <Grid columns={24}>
        {reviewsToRender.map((reviews, i) => {
          return (
            <Grid.Col
              span={{ base: 24, xs: 12, md: 8 }}
              // biome-ignore lint/suspicious/noArrayIndexKey: `i` is unique in this case
              key={`REVIEW_ROW-${i}`}
            >
              <Stack>
                {reviews.map((r) => (
                  <Review key={r.id} review={r} />
                ))}
              </Stack>
            </Grid.Col>
          );
        })}
      </Grid>

      <div ref={setLastElementRef} className="relative mb-20">
        <CircleLoader isLoading={isLoading} className="px-10 py-8" />
      </div>
    </div>
  );
};

export default ReviewList;
